import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Component from '../../components/Component';
import {
  createSuperOverDailyChallenge,
  deleteSuperOverDailyChallenge,
  getSuperOverDailyChallenge,
  getSuperOverDailyPayload,
  isSuperOverDailyApiSuccess,
  listSuperOverDailyChallenges,
  patchSuperOverDailyChallenge,
} from '../../services/superOverDailyApi';
import {
  addDaysToDateString,
  buildChallengePayload,
  CHALLENGE_TYPE_LABELS,
  countEnabledForDate,
  emptyChallengeForm,
  enabledCountByDate,
  formatSuccessConfigSummary,
  getDefaultSuccessConfigFields,
  getIstDateString,
  challengeToForm,
  resolveObjectivesPreview,
} from './superOverDailyUtils';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

const PAGE_SIZE = 50;

function SuccessConfigFields({ challengeType, fields, onChange, disabled }) {
  const setField = (key, value) => {
    onChange({ ...fields, [key]: value });
  };

  switch (challengeType) {
    case 'chase_target':
      return (
        <>
          <TextField
            label="Target runs"
            type="number"
            value={fields.target_runs ?? ''}
            onChange={(e) => setField('target_runs', e.target.value)}
            required
            fullWidth
            disabled={disabled}
            inputProps={{ min: 0 }}
          />
          <TextField
            label="Max balls (optional)"
            type="number"
            value={fields.max_balls ?? ''}
            onChange={(e) => setField('max_balls', e.target.value)}
            fullWidth
            disabled={disabled}
            inputProps={{ min: 0 }}
            helperText="Leave empty for no ball cap"
          />
        </>
      );
    case 'hit_sixes':
      return (
        <TextField
          label="Sixes required"
          type="number"
          value={fields.sixes ?? ''}
          onChange={(e) => setField('sixes', e.target.value)}
          required
          fullWidth
          disabled={disabled}
          inputProps={{ min: 0 }}
        />
      );
    case 'consecutive_sixes':
      return (
        <TextField
          label="Consecutive sixes"
          type="number"
          value={fields.consecutive ?? ''}
          onChange={(e) => setField('consecutive', e.target.value)}
          required
          fullWidth
          disabled={disabled}
          inputProps={{ min: 0 }}
        />
      );
    case 'score_in_balls':
      return (
        <>
          <TextField
            label="Runs"
            type="number"
            value={fields.runs ?? ''}
            onChange={(e) => setField('runs', e.target.value)}
            required
            fullWidth
            disabled={disabled}
            inputProps={{ min: 0 }}
          />
          <TextField
            label="Balls"
            type="number"
            value={fields.balls ?? ''}
            onChange={(e) => setField('balls', e.target.value)}
            required
            fullWidth
            disabled={disabled}
            inputProps={{ min: 1 }}
          />
        </>
      );
    case 'take_wickets':
      return (
        <>
          <TextField
            label="Wickets"
            type="number"
            value={fields.wickets ?? ''}
            onChange={(e) => setField('wickets', e.target.value)}
            required
            fullWidth
            disabled={disabled}
            inputProps={{ min: 0 }}
          />
          <TextField
            label="Max balls (optional)"
            type="number"
            value={fields.max_balls ?? ''}
            onChange={(e) => setField('max_balls', e.target.value)}
            fullWidth
            disabled={disabled}
            inputProps={{ min: 0 }}
          />
        </>
      );
    case 'defend_runs':
      return (
        <>
          <TextField
            label="Max runs conceded"
            type="number"
            value={fields.max_conceded ?? ''}
            onChange={(e) => setField('max_conceded', e.target.value)}
            required
            fullWidth
            disabled={disabled}
            inputProps={{ min: 0 }}
          />
          <TextField
            label="Max balls (optional)"
            type="number"
            value={fields.max_balls ?? ''}
            onChange={(e) => setField('max_balls', e.target.value)}
            fullWidth
            disabled={disabled}
            inputProps={{ min: 1 }}
            helperText="Used for objectives copy and ball count (default 6)"
          />
        </>
      );
    case 'no_boundaries':
      return (
        <TextField
          select
          label="Role"
          value={fields.role ?? 'bowling'}
          onChange={(e) => setField('role', e.target.value)}
          required
          fullWidth
          disabled={disabled}
        >
          <MenuItem value="bowling">Bowling — no boundaries conceded</MenuItem>
          <MenuItem value="batting">Batting — no boundaries hit</MenuItem>
        </TextField>
      );
    case 'no_dot_balls':
      return (
        <TextField
          label="Balls to face"
          type="number"
          value={fields.balls ?? ''}
          onChange={(e) => setField('balls', e.target.value)}
          required
          fullWidth
          disabled={disabled}
          inputProps={{ min: 1 }}
          helperText="Every ball must score ≥ 1 run (no dot balls)"
        />
      );
    case 'no_wicket_survival':
      return (
        <TextField
          label="Max balls to face"
          type="number"
          value={fields.max_balls ?? ''}
          onChange={(e) => setField('max_balls', e.target.value)}
          required
          fullWidth
          disabled={disabled}
          inputProps={{ min: 1 }}
          helperText="Must not lose a wicket in these balls"
        />
      );
    default:
      return null;
  }
}

function ArcadeCardPreview({ form }) {
  const summary = formatSuccessConfigSummary(form.challenge_type, form.success_config_fields);
  const badgeLabel = form.badge?.trim() || 'Daily Challenge';

  return (
    <Paper variant="outlined" sx={{ p: 2, bgcolor: '#1a1a2e', color: '#fff', borderRadius: 2 }}>
      <Typography variant="overline" sx={{ opacity: 0.7 }}>
        Arcade list card
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
        <Chip size="small" label={badgeLabel} sx={{ bgcolor: '#e67e22', color: '#fff' }} />
        <Chip size="small" label={`${form.reward_fe || 0} FE`} variant="outlined" sx={{ color: '#fff', borderColor: '#fff' }} />
      </Box>
      <Typography variant="h6" sx={{ mt: 1 }}>
        {form.title || 'Challenge title'}
      </Typography>
      <Typography variant="body2" sx={{ opacity: 0.85, mt: 0.5 }}>
        {form.objective || 'Tagline'}
      </Typography>
      <Typography variant="caption" display="block" sx={{ mt: 1, opacity: 0.7 }}>
        {summary}
      </Typography>
      <Button variant="contained" size="small" sx={{ mt: 1.5, bgcolor: '#e67e22' }} disabled>
        {form.cta_text || 'Play'}
      </Button>
    </Paper>
  );
}

function ObjectivesScreenPreview({ form }) {
  const previewText = resolveObjectivesPreview(form);
  const countdown = form.countdown_seconds || '15';
  const usingCustom = Boolean(form.objectives?.trim());

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Typography variant="overline" color="text.secondary">
        Dugout Daily objectives screen
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mt: 1 }}>
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            border: '3px solid #e67e22',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Typography variant="h6" fontWeight={700}>
            {countdown}
          </Typography>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6">{form.title || 'Title'}</Typography>
          <Typography variant="body2" color="text.secondary">
            {form.objective || 'Tagline under icon'}
          </Typography>
        </Box>
      </Box>
      <Divider sx={{ my: 1.5 }} />
      {previewText ? (
        <>
          {usingCustom && (
            <Chip size="small" label="Custom markdown" color="primary" sx={{ mb: 1 }} />
          )}
          {!usingCustom && form.objectives_preview && (
            <Chip size="small" label="Auto-generated (from API)" variant="outlined" sx={{ mb: 1 }} />
          )}
          <Typography
            component="pre"
            variant="body2"
            sx={{
              whiteSpace: 'pre-wrap',
              fontFamily: 'inherit',
              m: 0,
              bgcolor: 'action.hover',
              p: 1.5,
              borderRadius: 1,
              maxHeight: 240,
              overflow: 'auto',
            }}
          >
            {previewText}
          </Typography>
        </>
      ) : (
        <Typography variant="body2" color="text.secondary">
          Save or load a challenge to see auto-generated objectives preview from the API.
        </Typography>
      )}
      <Button variant="contained" fullWidth sx={{ mt: 2 }} disabled>
        Continue
      </Button>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, textAlign: 'center' }}>
        Continue label is fixed in the app
      </Typography>
    </Paper>
  );
}

function ChallengeFormDrawer({
  open,
  mode,
  form,
  setForm,
  challengeTypes,
  saving,
  formError,
  enabledOnSameDate,
  onClose,
  onSave,
}) {
  const handleTypeChange = (nextType) => {
    setForm((prev) => ({
      ...prev,
      challenge_type: nextType,
      success_config_fields: getDefaultSuccessConfigFields(nextType),
      objectives_preview: '',
    }));
  };

  const clearCustomObjectives = () => {
    setForm((prev) => ({ ...prev, objectives: '', objectives_preview: prev.objectives_preview }));
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 520, md: 560 }, p: 0 } }}
    >
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2, height: '100%', overflow: 'auto' }}>
        <Typography variant="h6">{mode === 'create' ? 'Create challenge' : 'Edit challenge'}</Typography>
        <Typography variant="body2" color="text.secondary">
          Active date uses IST. Multiple enabled challenges per day are allowed (ordered by sort_order ASC).
        </Typography>

        {formError && <Alert severity="error">{formError}</Alert>}
        {form.enabled && enabledOnSameDate > 0 && (
          <Alert severity="info">
            {enabledOnSameDate} other enabled challenge{enabledOnSameDate === 1 ? '' : 's'} already on{' '}
            {form.active_date}. Sort order controls display order.
          </Alert>
        )}

        <Typography variant="subtitle2">Challenge rules</Typography>
        <TextField
          select
          label="Challenge type"
          value={form.challenge_type}
          onChange={(e) => handleTypeChange(e.target.value)}
          required
          fullWidth
          disabled={saving}
        >
          {(challengeTypes.length ? challengeTypes : Object.keys(CHALLENGE_TYPE_LABELS)).map((type) => (
            <MenuItem key={type} value={type}>
              {CHALLENGE_TYPE_LABELS[type] || type}
            </MenuItem>
          ))}
        </TextField>
        <SuccessConfigFields
          challengeType={form.challenge_type}
          fields={form.success_config_fields}
          onChange={(fields) => setForm((prev) => ({ ...prev, success_config_fields: fields }))}
          disabled={saving}
        />

        <TextField
          label="Active date (IST)"
          type="date"
          value={form.active_date}
          onChange={(e) => setForm((prev) => ({ ...prev, active_date: e.target.value }))}
          required
          fullWidth
          disabled={saving}
          InputLabelProps={{ shrink: true }}
        />

        <Divider />
        <Typography variant="subtitle2">Objectives screen (Dugout Daily)</Typography>
        <TextField
          label="Title"
          value={form.title}
          onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
          required
          fullWidth
          disabled={saving}
          helperText="Objectives screen heading (e.g. Dhoni challenge)"
        />
        <TextField
          label="Objective / tagline"
          value={form.objective}
          onChange={(e) => setForm((prev) => ({ ...prev, objective: e.target.value }))}
          required
          fullWidth
          multiline
          minRows={2}
          disabled={saving}
          helperText="Shown under icon and in the rules card intro"
        />
        <TextField
          label="Hero / card image URL"
          value={form.image_url}
          onChange={(e) => setForm((prev) => ({ ...prev, image_url: e.target.value }))}
          fullWidth
          disabled={saving}
          placeholder="https://..."
        />
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            label="Countdown (seconds)"
            type="number"
            value={form.countdown_seconds}
            onChange={(e) => setForm((prev) => ({ ...prev, countdown_seconds: e.target.value }))}
            fullWidth
            disabled={saving}
            inputProps={{ min: 1, max: 120 }}
            helperText="1–120, default 15"
          />
          <TextField
            label="Badge"
            value={form.badge}
            onChange={(e) => setForm((prev) => ({ ...prev, badge: e.target.value }))}
            fullWidth
            disabled={saving}
            placeholder="Daily Challenge"
            helperText="Empty → Daily Challenge"
          />
        </Box>
        <TextField
          label="How to play"
          value={form.how_to_play}
          onChange={(e) => setForm((prev) => ({ ...prev, how_to_play: e.target.value }))}
          fullWidth
          multiline
          minRows={2}
          disabled={saving}
          helperText="Used when objectives markdown is empty (role default if blank)"
        />
        <TextField
          label="Objectives markdown (optional override)"
          value={form.objectives}
          onChange={(e) => setForm((prev) => ({ ...prev, objectives: e.target.value }))}
          fullWidth
          multiline
          minRows={6}
          disabled={saving}
          placeholder="Leave empty to auto-generate from objective + ball count + how_to_play"
        />
        {mode === 'edit' && form.objectives?.trim() && (
          <Button size="small" variant="outlined" onClick={clearCustomObjectives} disabled={saving}>
            Clear custom markdown (use auto-generate)
          </Button>
        )}

        <ObjectivesScreenPreview form={form} />

        <Divider />
        <Typography variant="subtitle2">Arcade &amp; rewards</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            label="Reward FE"
            type="number"
            value={form.reward_fe}
            onChange={(e) => setForm((prev) => ({ ...prev, reward_fe: e.target.value }))}
            fullWidth
            disabled={saving}
            inputProps={{ min: 0 }}
          />
          <TextField
            label="Energy cost"
            type="number"
            value={form.energy_cost}
            onChange={(e) => setForm((prev) => ({ ...prev, energy_cost: e.target.value }))}
            fullWidth
            disabled={saving}
            inputProps={{ min: 0 }}
            helperText="0 = free entry"
          />
        </Box>
        <TextField
          label="Arcade CTA text"
          value={form.cta_text}
          onChange={(e) => setForm((prev) => ({ ...prev, cta_text: e.target.value }))}
          fullWidth
          disabled={saving}
          helperText="Arcade list button (objectives screen uses fixed Continue)"
        />
        <TextField
          label="Sort order"
          type="number"
          value={form.sort_order}
          onChange={(e) => setForm((prev) => ({ ...prev, sort_order: e.target.value }))}
          fullWidth
          disabled={saving}
          helperText="Lower = first when multiple enabled on same day"
        />
        <TextField
          label="Metadata (JSON)"
          value={form.metadata_json}
          onChange={(e) => setForm((prev) => ({ ...prev, metadata_json: e.target.value }))}
          fullWidth
          multiline
          minRows={2}
          disabled={saving}
          placeholder='{"design_tag": "ipl"}'
        />

        <FormControlLabel
          control={
            <Switch
              checked={form.enabled}
              onChange={(e) => setForm((prev) => ({ ...prev, enabled: e.target.checked }))}
              disabled={saving}
            />
          }
          label="Enabled"
        />

        <ArcadeCardPreview form={form} />

        <Box sx={{ display: 'flex', gap: 1, mt: 'auto', pt: 2 }}>
          <Button variant="outlined" onClick={onClose} disabled={saving} fullWidth>
            Cancel
          </Button>
          <Button variant="contained" onClick={onSave} disabled={saving} fullWidth>
            {saving ? <CircularProgress size={22} /> : mode === 'create' ? 'Create' : 'Save changes'}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}

function SuperOverDailyChallenge() {
  const todayIst = getIstDateString();
  const defaultTo = addDaysToDateString(todayIst, 30);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [challengeTypes, setChallengeTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [fromDate, setFromDate] = useState(addDaysToDateString(todayIst, -7));
  const [toDate, setToDate] = useState(defaultTo);
  const [enabledFilter, setEnabledFilter] = useState('ALL');
  const [offset, setOffset] = useState(0);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState('create');
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyChallengeForm());
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const handleSessionExpired = useCallback(
    (res) => {
      if (String(res?.statusCode || '') === '10003') {
        showSnackbar('Session expired. Please login again.', 'error');
        setTimeout(() => {
          window.location = '/login';
        }, 1500);
        return true;
      }
      return false;
    },
    [showSnackbar]
  );

  const handleApiError = useCallback(
    (res, fallback) => {
      if (handleSessionExpired(res)) return true;
      if (!isSuperOverDailyApiSuccess(res)) {
        setError(res?.message || fallback);
        return true;
      }
      return false;
    },
    [handleSessionExpired]
  );

  const enabledFilterValue = useMemo(() => {
    if (enabledFilter === 'ENABLED') return true;
    if (enabledFilter === 'DISABLED') return false;
    return undefined;
  }, [enabledFilter]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await listSuperOverDailyChallenges({
        from: fromDate || undefined,
        to: toDate || undefined,
        enabled: enabledFilterValue,
        limit: PAGE_SIZE,
        offset,
      });
      if (handleApiError(res, 'Failed to load challenges.')) {
        setRows([]);
        setTotal(0);
        return;
      }
      const data = getSuperOverDailyPayload(res);
      setRows(data?.rows || []);
      setTotal(data?.total ?? 0);
      if (Array.isArray(data?.challenge_types) && data.challenge_types.length) {
        setChallengeTypes(data.challenge_types);
      }
    } catch (fetchError) {
      setError(fetchError.message || 'Failed to load challenges.');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [enabledFilterValue, fromDate, handleApiError, offset, toDate]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const hasMore = offset + rows.length < total;
  const hasPrev = offset > 0;

  const enabledByDate = useMemo(() => enabledCountByDate(rows), [rows]);

  const enabledOnSameDate = useMemo(
    () => countEnabledForDate(rows, form.active_date, editingId),
    [rows, form.active_date, editingId]
  );

  const openCreate = () => {
    setDrawerMode('create');
    setEditingId(null);
    setForm(emptyChallengeForm());
    setFormError('');
    setDrawerOpen(true);
  };

  const openEdit = async (row) => {
    setDrawerMode('edit');
    setEditingId(row.id);
    setFormError('');
    setDrawerOpen(true);
    setLoadingEdit(true);
    try {
      const res = await getSuperOverDailyChallenge(row.id);
      if (handleSessionExpired(res)) return;
      if (!isSuperOverDailyApiSuccess(res)) {
        setFormError(res?.message || 'Failed to load challenge.');
        setForm(challengeToForm(row));
        return;
      }
      const data = getSuperOverDailyPayload(res);
      setForm(challengeToForm(data || row));
    } catch {
      setFormError('Failed to load challenge.');
      setForm(challengeToForm(row));
    } finally {
      setLoadingEdit(false);
    }
  };

  const closeDrawer = () => {
    if (saving) return;
    setDrawerOpen(false);
    setFormError('');
  };

  const handleSave = async () => {
    setFormError('');
    const built = buildChallengePayload(form);
    if (built.error) {
      setFormError(built.error);
      return;
    }

    setSaving(true);
    try {
      const res =
        drawerMode === 'create'
          ? await createSuperOverDailyChallenge(built.payload)
          : await patchSuperOverDailyChallenge(editingId, built.payload);

      if (handleSessionExpired(res)) return;
      if (!isSuperOverDailyApiSuccess(res)) {
        setFormError(res?.message || 'Save failed.');
        return;
      }

      showSnackbar(drawerMode === 'create' ? 'Challenge created.' : 'Challenge updated.');
      setDrawerOpen(false);
      fetchList();
    } catch {
      setFormError('Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const handleQuickToggle = async (row) => {
    const nextEnabled = !row.enabled;

    try {
      const res = await patchSuperOverDailyChallenge(row.id, { enabled: nextEnabled });
      if (handleSessionExpired(res)) return;
      if (!isSuperOverDailyApiSuccess(res)) {
        showSnackbar(res?.message || 'Failed to update.', 'error');
        return;
      }
      const updated = getSuperOverDailyPayload(res);
      setRows((prev) =>
        prev.map((item) => (item.id === row.id ? { ...item, ...(updated || {}), enabled: nextEnabled } : item))
      );
      showSnackbar(nextEnabled ? 'Challenge enabled.' : 'Challenge disabled.');
    } catch {
      showSnackbar('Failed to update.', 'error');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await deleteSuperOverDailyChallenge(deleteTarget.id);
      if (handleSessionExpired(res)) return;
      if (!isSuperOverDailyApiSuccess(res)) {
        showSnackbar(res?.message || 'Delete failed.', 'error');
        return;
      }
      showSnackbar('Challenge deleted.');
      setDeleteTarget(null);
      fetchList();
    } catch {
      showSnackbar('Delete failed.', 'error');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Component>
      <div className="page-body">
        <div className="container-fluid">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, mt: 1 }}>
            <Button component={Link} to="/admin/arcade" startIcon={<ArrowBackIcon />} size="small">
              Arcade
            </Button>
            <Typography variant="h5" sx={{ flex: 1 }}>
              Super Over Daily Challenge
            </Typography>
            <Tooltip title="Refresh">
              <IconButton onClick={fetchList} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
              Create challenge
            </Button>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Configure Dugout Daily objectives copy and Arcade cards for solo Super Over challenges (ball-by-ball PvAI).
            Multiple enabled challenges per IST day are supported; public APIs serve today&apos;s rows ordered by sort_order.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <Paper sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
              <TextField
                label="From (IST)"
                type="date"
                size="small"
                value={fromDate}
                onChange={(e) => {
                  setOffset(0);
                  setFromDate(e.target.value);
                }}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="To (IST)"
                type="date"
                size="small"
                value={toDate}
                onChange={(e) => {
                  setOffset(0);
                  setToDate(e.target.value);
                }}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                select
                label="Enabled"
                size="small"
                value={enabledFilter}
                onChange={(e) => {
                  setOffset(0);
                  setEnabledFilter(e.target.value);
                }}
                sx={{ minWidth: 140 }}
              >
                <MenuItem value="ALL">All</MenuItem>
                <MenuItem value="ENABLED">Enabled only</MenuItem>
                <MenuItem value="DISABLED">Disabled only</MenuItem>
              </TextField>
              <Chip size="small" label={`Today (IST): ${todayIst}`} variant="outlined" />
              <Chip size="small" label={`${total} total`} />
            </Box>
          </Paper>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Active date</TableCell>
                      <TableCell>Sort</TableCell>
                      <TableCell>Title</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Goal</TableCell>
                      <TableCell align="right">Reward</TableCell>
                      <TableCell>Countdown</TableCell>
                      <TableCell>Enabled</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">No challenges in this range.</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      rows.map((row) => (
                        <TableRow
                          key={row.id}
                          hover
                          sx={row.active_date === todayIst ? { bgcolor: 'action.hover' } : undefined}
                        >
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                              {row.active_date}
                              {row.active_date === todayIst && <Chip size="small" color="primary" label="Today" />}
                              {(enabledByDate[row.active_date] || 0) > 1 && (
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  label={`${enabledByDate[row.active_date]} enabled`}
                                />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>{row.sort_order ?? 0}</TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>
                              {row.title}
                            </Typography>
                            {row.badge && (
                              <Typography variant="caption" color="text.secondary">
                                {row.badge}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>{CHALLENGE_TYPE_LABELS[row.challenge_type] || row.challenge_type}</TableCell>
                          <TableCell sx={{ maxWidth: 200 }}>
                            <Typography variant="body2" noWrap title={formatSuccessConfigSummary(row.challenge_type, row.success_config)}>
                              {formatSuccessConfigSummary(row.challenge_type, row.success_config)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">{row.reward_fe ?? '—'} FE</TableCell>
                          <TableCell>{row.countdown_seconds ?? 15}s</TableCell>
                          <TableCell>
                            <Switch
                              size="small"
                              checked={Boolean(row.enabled)}
                              onChange={() => handleQuickToggle(row)}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <IconButton size="small" onClick={() => openEdit(row)} aria-label="Edit">
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setDeleteTarget(row)}
                              aria-label="Delete"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                <Button disabled={!hasPrev} onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}>
                  Previous
                </Button>
                <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
                  {total > 0 ? `${offset + 1}–${offset + rows.length} of ${total}` : '0 results'}
                </Typography>
                <Button disabled={!hasMore} onClick={() => setOffset((o) => o + PAGE_SIZE)}>
                  Next
                </Button>
              </Box>
            </>
          )}
        </div>
      </div>

      <ChallengeFormDrawer
        open={drawerOpen}
        mode={drawerMode}
        form={form}
        setForm={setForm}
        challengeTypes={challengeTypes}
        saving={saving || loadingEdit}
        formError={formError}
        enabledOnSameDate={enabledOnSameDate}
        onClose={closeDrawer}
        onSave={handleSave}
      />

      <Dialog open={Boolean(deleteTarget)} onClose={() => !deleting && setDeleteTarget(null)}>
        <DialogTitle>Delete challenge?</DialogTitle>
        <DialogContent>
          <Typography>
            Permanently delete &ldquo;{deleteTarget?.title}&rdquo; for {deleteTarget?.active_date}? This cascades attempts, rewards, and events. Prefer disabling for past days if you want history.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={deleting}>
            {deleting ? <CircularProgress size={22} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Component>
  );
}

export default SuperOverDailyChallenge;
